import { FunctionComponent } from "react";
import Head from "next/head";
import { useRouter } from "next/router";

const SITE_URL = "https://browserbite.io";
// Generated by scripts/generate-og-images.mjs — bump the -v suffix on redesigns
// so social scrapers (LinkedIn, WhatsApp, Slack) don't serve their cached card.
const DEFAULT_OG_IMAGES: Record<string, string> = {
  de: `${SITE_URL}/images/og/og-default-v2.png`,
  en: `${SITE_URL}/images/og/og-default-en-v2.png`
};

interface SeoProps {
  title: string;
  description: string;
  /** Path without locale prefix, e.g. "/" or "/case-studies" */
  path: string;
  image?: string;
}

const localizedUrl = (locale: string, path: string): string => {
  const suffix = path === "/" ? "" : path;
  return locale === "de" ? `${SITE_URL}${suffix || "/"}` : `${SITE_URL}/en${suffix}`;
};

// Meta/OG descriptions should stay ≤160 chars; dynamic sources (case-study
// summaries, CMS blog excerpts) are longer UI copy, so cut at a word boundary.
const MAX_DESCRIPTION_LENGTH = 160;

const truncateDescription = (text: string): string => {
  if (text.length <= MAX_DESCRIPTION_LENGTH) return text;
  const cut = text.slice(0, MAX_DESCRIPTION_LENGTH - 1);
  return `${cut.slice(0, cut.lastIndexOf(" "))}…`;
};

const Seo: FunctionComponent<SeoProps> = ({ title, description, path, image }) => {
  const { locale = "de" } = useRouter();
  const url = localizedUrl(locale, path);
  const metaDescription = truncateDescription(description);
  const ogImage = image
    ? `${SITE_URL}${image}`
    : DEFAULT_OG_IMAGES[locale] ?? DEFAULT_OG_IMAGES.de;

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={metaDescription} />

      <meta property="og:title" content={title} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Browserbite" />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      {!image && (
        <>
          <meta property="og:image:width" content="2400" />
          <meta property="og:image:height" content="1260" />
        </>
      )}
      <meta property="og:image:alt" content={title} />
      <meta property="og:locale" content={locale === "de" ? "de_DE" : "en_US"} />
      <meta name="twitter:card" content="summary_large_image" />

      <link rel="canonical" href={url} />
      <link rel="alternate" hrefLang="de" href={localizedUrl("de", path)} />
      <link rel="alternate" hrefLang="en" href={localizedUrl("en", path)} />
      <link rel="alternate" hrefLang="x-default" href={localizedUrl("de", path)} />

      {
        path === "/" &&
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              name: "Browserbite EOOD",
              url: SITE_URL,
              logo: `${SITE_URL}/images/common/logo/browserbite_lowercase_transparent_rebuilt_dark.svg`,
              description,
              address: {
                "@type": "PostalAddress",
                streetAddress: "ul. \"Hristo Belchev\" 44, Fl. 1",
                postalCode: "1000",
                addressLocality: "Sofia",
                addressCountry: "BG"
              },
              sameAs: [
                "https://www.linkedin.com/company/browserbite-ltd/",
                "https://clutch.co/profile/browserbite-ood"
              ]
            })
          }}
        />
      }
    </Head>
  );
};

export default Seo;
